import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  User, 
  SignOut, 
  CalendarBlank, 
  Users, 
  House,
  List,
  Confetti,
  Trophy
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import NotificationBell from './NotificationBell';
import MemberBadge from './MemberBadge';

const Header = ({ onMenuClick }) => {
  const { user, logout, language, setLanguage, t } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === 'admin' || user?.role === 'staff';
  const isTrainer = user?.role === 'trainer';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = user ? (
    isTrainer
      ? [
          { path: '/trainer/schedule', label: language === 'de' ? 'Wochenplan' : 'Schedule', icon: CalendarBlank },
          { path: '/book', label: t('book'), icon: CalendarBlank },
          ...(user.manages_community ? [{ path: '/community', label: 'Community', icon: Confetti }] : []),
        ]
      : [
          { path: '/book', label: t('book'), icon: CalendarBlank },
          { path: '/matches', label: t('matches'), icon: Users },
          { path: '/trainings', label: language === 'de' ? 'Trainings & Kurse' : 'Trainings & Classes', icon: Trophy },
          { path: '/community', label: 'Community', icon: Confetti },
        ]
  ) : [];

  return (
    <>
    <header 
      className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200"
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-2">
            {/* Hamburger menu for admin/trainer on mobile */}
            {user && (isAdmin || isTrainer) && (
              <button onClick={onMenuClick} className="lg:hidden p-2 -ml-2 text-slate-600 hover:bg-slate-100 rounded-lg" data-testid="mobile-menu-btn">
                <List size={24} weight="bold" />
              </button>
            )}
            <Link 
              to={user ? (isAdmin ? (user.role === 'staff' ? '/admin/bookings' : '/admin') : isTrainer ? '/trainer/schedule' : '/dashboard') : '/'} 
              className="flex items-center gap-3"
              data-testid="logo-link"
            >
              <img src="/logo.png" alt="Padelarena Wädenswil" className="h-10 w-auto flex-shrink-0" />
              <div className="hidden sm:flex flex-col leading-none">
                <span className="text-slate-900 font-bold text-base tracking-tight">
                  Padelarena Wädenswil
                </span>
                <span className="text-[10px] text-emerald-600 font-semibold tracking-wider uppercase mt-0.5">
                  Padlr. · Booking
                </span>
              </div>
            </Link>
          </div>

          {/* Navigation Links - Desktop */}
          {user && !isAdmin && (
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                    location.pathname === path 
                      ? 'text-emerald-600' 
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                  data-testid={`nav-${path.slice(1)}`}
                >
                  <Icon size={18} weight="duotone" />
                  {label}
                </Link>
              ))}
            </nav>
          )}

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={() => setLanguage(language === 'en' ? 'de' : 'en')}
              className="text-slate-400 hover:text-slate-700 transition-colors text-sm font-medium px-2 py-1 rounded-md hover:bg-slate-100"
              data-testid="language-toggle"
            >
              {language === 'en' ? 'DE' : 'EN'}
            </button>

            {user ? (
              <>
                {/* Credits Display */}
                <div className="hidden sm:flex items-center gap-2 text-sm bg-emerald-50 px-3 py-1.5 rounded-lg">
                  <span className="text-slate-500">{t('credits')}:</span>
                  <span className="text-emerald-600 font-bold">
                    {user.credits_balance?.toFixed(2)} CHF
                  </span>
                </div>

                {/* Notifications */}
                <NotificationBell />

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 text-slate-700 hover:bg-slate-100"
                      data-testid="user-menu-trigger"
                    >
                      <div className="w-8 h-8 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-center">
                        <User size={18} weight="duotone" className="text-emerald-600" />
                      </div>
                      <span className="hidden sm:flex items-center gap-1 text-sm font-medium">{user.name}<MemberBadge user={user} size={13} /></span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="end" 
                    className="w-56 bg-white border-slate-200 shadow-lg"
                  >
                    <div className="px-3 py-2 border-b border-slate-100">
                      <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5">{user.name}<MemberBadge user={user} size={13} /></p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                      <p className="text-xs text-emerald-600 mt-1 font-medium">
                        {t('level')}: {user.player_level}
                      </p>
                    </div>
                    <DropdownMenuItem 
                      onClick={() => navigate('/profile')}
                      className="cursor-pointer hover:bg-slate-50"
                      data-testid="menu-profile"
                    >
                      <User className="mr-2" size={16} />
                      {t('profile')}
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem 
                        onClick={() => navigate(user.role === 'staff' ? '/admin/bookings' : '/admin')}
                        className="cursor-pointer hover:bg-slate-50"
                        data-testid="menu-admin"
                      >
                        <List className="mr-2" size={16} />
                        {t('admin')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-slate-100" />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="cursor-pointer text-red-500 hover:bg-red-50"
                      data-testid="menu-logout"
                    >
                      <SignOut className="mr-2" size={16} />
                      {t('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link 
                  to="/login"
                  className="text-slate-500 hover:text-slate-700 font-medium text-sm"
                  data-testid="login-link"
                >
                  {t('login')}
                </Link>
                <Link 
                  to="/register"
                  className="btn-primary text-sm py-2 px-4"
                  data-testid="register-link"
                >
                  {t('register')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation - Customer only (bottom bar pattern)
          NOTE: This nav must live OUTSIDE the <header> element. The header uses
          backdrop-blur-md (backdrop-filter), which makes it a CSS containing
          block for fixed-positioned descendants — that would otherwise trap
          this nav at the top of the screen instead of the viewport bottom.

          Labels are intentionally single-word so 5 tabs fit cleanly on a
          375-430px iPhone viewport without wrapping. */}
    </header>
      {user && !isAdmin && !isTrainer && (
        <nav
          style={{ zIndex: 10000 }}
          className="md:hidden fixed bottom-2 left-2 right-2 rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-md shadow-lg shadow-slate-900/10 px-1 py-1 flex justify-around mb-[env(safe-area-inset-bottom)]"
          data-testid="mobile-bottom-nav">
          {[
            { path: '/book', label: language === 'de' ? 'Buchen' : 'Book', icon: CalendarBlank },
            { path: '/matches', label: language === 'de' ? 'Matches' : 'Matches', icon: Users },
            { path: '/trainings', label: language === 'de' ? 'Training' : 'Training', icon: Trophy },
            { path: '/community', label: 'Community', icon: Confetti },
            { path: '/profile', label: language === 'de' ? 'Profil' : 'Profile', icon: User },
          ].map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              className={`flex-1 min-w-0 flex flex-col items-center gap-0.5 py-1.5 px-1 rounded-xl transition-colors ${
                location.pathname === path ? 'text-emerald-600 bg-emerald-50' : 'text-slate-400 hover:text-slate-700'
              }`}
              data-testid={`mobile-nav-${path.slice(1)}`}
            >
              <Icon size={22} weight={location.pathname === path ? 'fill' : 'duotone'} />
              <span className="text-[10px] font-medium leading-tight whitespace-nowrap">{label}</span>
            </Link>
          ))}
        </nav>
      )}
    </>
  );
};

export default Header;
